// ─── Écran HISTORIQUE : journal des changements d'organisation ────
// Anti-chronologique, groupé par jour. Alimenté en BEST-EFFORT par
// `journaliser()` (Core) depuis les services d'événements/récurrences/EDT/
// garde/demandes — porté de MoPCal (`screens/HistoriqueScreen.js`), adapté
// aux conventions de la suite (Feuille plutôt que Modal, emoji plutôt
// qu'Ionicons, `onErreur` remonté plutôt qu'avalé).
// Lecture seule : rien ne s'édite ici.
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, ScrollView, StyleSheet } from 'react-native';
import { PageHeader, EmptyState, Feuille, GesteRetourEcran, useUI, useT, ecouterHistorique } from '@mop/core';

const TYPE_META = {
  evenement:  { emoji: '📅', couleur: '#6C4FE6' },
  recurrence: { emoji: '🔁', couleur: '#0277BD' },
  edt:        { emoji: '🕐', couleur: '#2E7D32' },
  garde:      { emoji: '🔄', couleur: '#EF6C00' },
  demande:    { emoji: '🧷', couleur: '#8A7CCB' },
};
const ACTION_COULEUR = { creation: '#2E7D32', modification: '#EF6C00', suppression: '#E53935' };
const FILTRES = ['tous', 'evenement', 'recurrence', 'edt', 'garde', 'demande'];

const deux = (n) => String(n).padStart(2, '0');
const heureISO = (iso) => { const d = new Date(iso); return isNaN(d) ? '' : `${deux(d.getHours())}:${deux(d.getMinutes())}`; };
const jjmm = (dk) => { const p = String(dk || '').split('-'); return p.length < 3 ? (dk || '') : `${p[2]}/${p[1]}`; };
const jourDeISO = (iso) => { const d = new Date(iso); return isNaN(d) ? '' : `${d.getFullYear()}-${deux(d.getMonth() + 1)}-${deux(d.getDate())}`; };
const complet = (iso) => { const d = new Date(iso); return isNaN(d) ? '' : `${deux(d.getDate())}/${deux(d.getMonth() + 1)}/${d.getFullYear()} · ${deux(d.getHours())}:${deux(d.getMinutes())}`; };
const dkFr = (dk) => { const p = String(dk || '').split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : (dk || ''); };

export default function EcranHistorique({ calId, onFermer }) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);

  const [entrees, setEntrees] = useState([]);
  const [filtre, setFiltre] = useState('tous');
  const [detail, setDetail] = useState(null);
  // Une écoute refusée renvoyait une liste vide EN SILENCE : l'écran affichait
  // « aucun changement » alors qu'il n'était pas lisible (même patron que
  // Contacts/Journal/messagerie).
  const [erreurLecture, setErreurLecture] = useState('');

  useEffect(() => {
    if (!calId) { setEntrees([]); return undefined; }
    setErreurLecture('');
    return ecouterHistorique(calId, setEntrees, 300, setErreurLecture);
  }, [calId]);

  const groupes = useMemo(() => {
    const filtrees = filtre === 'tous' ? entrees : entrees.filter(e => e.type === filtre);
    const out = [];
    let courant = null;
    for (const e of filtrees) {
      const jk = jourDeISO(e.le);
      if (!courant || courant.jour !== jk) { courant = { jour: jk, items: [] }; out.push(courant); }
      courant.items.push(e);
    }
    return out;
  }, [entrees, filtre]);

  const libelleJour = (jk) => {
    const now = new Date();
    const auj = `${now.getFullYear()}-${deux(now.getMonth() + 1)}-${deux(now.getDate())}`;
    const h = new Date(now); h.setDate(now.getDate() - 1);
    const hier = `${h.getFullYear()}-${deux(h.getMonth() + 1)}-${deux(h.getDate())}`;
    if (jk === auj) return t('historique.aujourdhui');
    if (jk === hier) return t('historique.hier');
    return dkFr(jk);
  };
  const titrePrincipal = (e) => (e.type === 'garde' ? t('historique.gardeItem') : (e.titre || t('historique.sansTitre')));
  const libelleAction = (e) => {
    if (e.detail === 'activation') return t('historique.act_activation');
    if (e.detail === 'desactivation') return t('historique.act_desactivation');
    if (e.detail === 'exceptions') return t('historique.act_exceptions');
    return t(`historique.action_${e.action}`);
  };
  const sousTitre = (e) => {
    const bouts = [libelleAction(e), t(`historique.type_${e.type}`)];
    if (e.parNom) bouts.push(t('historique.par', { nom: e.parNom }));
    return bouts.join(' · ');
  };
  const deplacement = (e) => (e.detail && typeof e.detail === 'object' && e.detail.champ === 'date') ? e.detail : null;

  return (
    <GesteRetourEcran actif={!!onFermer} onRetour={onFermer}>
      <View style={s.page}>
        <PageHeader titre={t('historique.titre')} iconeTitre="🕰️" onRetour={onFermer} />

        <View style={s.filtres}>
          {FILTRES.map(f => {
            const on = filtre === f;
            return (
              <TouchableOpacity key={f} style={[s.chip, on && s.chipOn]} activeOpacity={0.7} onPress={() => setFiltre(f)}>
                <Text style={[s.chipTxt, on && s.chipTxtOn]}>{t(`historique.filtre_${f}`)}</Text>
              </TouchableOpacity>
            );
          })}
        </View>

        <ScrollView contentContainerStyle={s.corps}>
          {!!erreurLecture && (
            <View style={s.bandeau}>
              <Text style={s.bandeauTxt}>⚠️ {t('historique.vide')}</Text>
              <Text style={s.bandeauDet}>{erreurLecture}</Text>
            </View>
          )}
          {erreurLecture ? null : groupes.length === 0 ? (
            <EmptyState icon="🕰️" texte={t('historique.vide')} aide={t('historique.videAide')} />
          ) : groupes.map(g => (
            <View key={g.jour} style={{ marginBottom: 6 }}>
              <Text style={s.jourLbl}>{libelleJour(g.jour)}</Text>
              {g.items.map(e => {
                const meta = TYPE_META[e.type] || TYPE_META.evenement;
                const dep = deplacement(e);
                return (
                  <TouchableOpacity key={e.id} style={s.ligne} activeOpacity={0.7} onPress={() => setDetail(e)}>
                    <View style={[s.iconeWrap, { backgroundColor: meta.couleur + '1A' }]}>
                      <Text style={{ fontSize: 17 }}>{meta.emoji}</Text>
                      <View style={[s.pastille, { backgroundColor: ACTION_COULEUR[e.action] || UI.muted }]} />
                    </View>
                    <View style={{ flex: 1 }}>
                      <Text style={s.ligneTitre} numberOfLines={1}>{titrePrincipal(e)}</Text>
                      <Text style={s.ligneSous} numberOfLines={2}>{sousTitre(e)}</Text>
                      {dep ? (
                        <Text style={s.ligneDate}>{t('historique.deplaceDe', { de: jjmm(dep.ancien), vers: jjmm(dep.nouveau) })}</Text>
                      ) : !!e.dateConcernee && e.type !== 'garde' ? (
                        <Text style={s.ligneDate}>{t('historique.pourLe', { date: jjmm(e.dateConcernee) })}</Text>
                      ) : (e.type === 'garde' && !!e.dateConcernee) ? (
                        <Text style={s.ligneDate}>{jjmm(e.dateConcernee)}</Text>
                      ) : null}
                    </View>
                    <Text style={s.ligneHeure}>{heureISO(e.le)}</Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          ))}
        </ScrollView>

        <Feuille visible={!!detail} titre={detail ? titrePrincipal(detail) : ''}
          emoji={detail ? (TYPE_META[detail.type] || TYPE_META.evenement).emoji : '🕰️'}
          onFermer={() => setDetail(null)}>
          {!!detail && (
            <>
              <Ligne s={s} label={t('historique.dAction')} val={libelleAction(detail)} />
              <Ligne s={s} label={t('historique.dType')} val={t(`historique.type_${detail.type}`)} />
              {!!detail.dateConcernee && <Ligne s={s} label={t('historique.dDateConcernee')} val={dkFr(detail.dateConcernee)} />}
              {!!deplacement(detail) && (
                <Ligne s={s} label={t('historique.dDeplacement')}
                  val={t('historique.deplaceDe', { de: jjmm(deplacement(detail).ancien), vers: jjmm(deplacement(detail).nouveau) })} />
              )}
              {!!detail.parNom && <Ligne s={s} label={t('historique.dPar')} val={detail.parNom} />}
              <Ligne s={s} label={t('historique.dQuand')} val={complet(detail.le)} />
              <Text style={s.note}>{t('historique.detailNote')}</Text>
            </>
          )}
        </Feuille>
      </View>
    </GesteRetourEcran>
  );
}

function Ligne({ s, label, val }) {
  return (
    <View style={s.kvRow}>
      <Text style={s.kvLabel}>{label}</Text>
      <Text style={s.kvVal}>{val}</Text>
    </View>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  page: { flex: 1, backgroundColor: UI.bg },
  filtres: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, paddingHorizontal: 12, paddingTop: 8, paddingBottom: 8 },
  chip: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 16, backgroundColor: UI.champ, borderWidth: 0.5, borderColor: UI.champBord },
  chipOn: { backgroundColor: UI.accent, borderColor: UI.accent },
  chipTxt: { fontSize: 12, fontWeight: '700', color: UI.muted },
  chipTxtOn: { color: '#fff' },
  corps: { padding: 12, paddingTop: 2, paddingBottom: 30 },
  jourLbl: { fontSize: 12, fontWeight: '800', color: UI.accent, marginTop: 10, marginBottom: 6, marginLeft: 2, textTransform: 'uppercase', letterSpacing: 0.4 },
  ligne: { flexDirection: 'row', alignItems: 'center', gap: 12, backgroundColor: UI.card, borderRadius: 14, padding: 12, marginBottom: 8, borderWidth: 0.5, borderColor: UI.border },
  iconeWrap: { width: 40, height: 40, borderRadius: 12, alignItems: 'center', justifyContent: 'center' },
  pastille: { position: 'absolute', right: -1, top: -1, width: 11, height: 11, borderRadius: 6, borderWidth: 2, borderColor: UI.card },
  ligneTitre: { fontSize: 14, fontWeight: '700', color: UI.ink },
  ligneSous: { fontSize: 12, color: UI.muted, marginTop: 1 },
  ligneDate: { fontSize: 12, color: UI.accent, marginTop: 2, fontWeight: '600' },
  ligneHeure: { fontSize: 12, color: UI.faint, fontWeight: '600' },
  kvRow: { flexDirection: 'row', alignItems: 'flex-start', paddingVertical: 8, borderBottomWidth: 0.5, borderBottomColor: UI.border },
  kvLabel: { width: 130, fontSize: 14, color: UI.muted, fontWeight: '600' },
  kvVal: { flex: 1, fontSize: 14, color: UI.ink, fontWeight: '600' },
  note: { fontSize: 12, color: UI.faint, marginTop: 12, lineHeight: 16 },
  bandeau: { backgroundColor: UI.dangerTint || '#fdecec', borderRadius: 12, borderWidth: 0.5, borderColor: UI.danger || '#e5484d', padding: 11, marginBottom: 10 },
  bandeauTxt: { fontSize: 13, fontWeight: '800', color: UI.danger || '#e5484d' },
  bandeauDet: { fontSize: 11.5, color: UI.muted, marginTop: 3 },
});
